import React from "react";
import * as teamsJs from "@microsoft/teams-js";
import { useTranslation } from "react-i18next";
import "./styles/components/team-request-form.css";
import { apiService } from "./services/api.service";
import { telemetryService } from "./services/telemetry.service";
import { HostTeamInfo, TeamTemplate, TeamPrivacyType, TeamPrivacy, AppSettings, QuotaModel, StorageQuotaMode } from "./types";
import PeoplePicker, { User } from "./PeoplePicker";
import NotificationModal from "./components/NotificationModal";

interface TeamRequest {
  teamName: string;
  description: string;
  privacy: TeamPrivacy;
  justification: string;
  owner: string;
  members: string;
  templateId?: string;
  quotaModelId?: string;
  requesterId: string;
  requesterEmail: string;
  teamId: string | null;
  teamsAppId?: string; // 🔗 DEEP LINK: ID de l'application Teams pour les notifications d'approbation
}

interface TeamRequestFormProps {
  currentUserName: string;
  currentUserId: string;
  currentUserEmail: string;
  teamId: string | null;
  tenantError?: string; // ✨ MULTI-TENANT: Error message if tenant is not configured
}

export default function TeamRequestForm({ currentUserName, currentUserId, currentUserEmail, teamId, tenantError }: TeamRequestFormProps) {
  const { t } = useTranslation(['teamRequestForm', 'common']);
  const [templates, setTemplates] = React.useState<TeamTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = React.useState(false);
  const [teamInfo, setTeamInfo] = React.useState<HostTeamInfo | null>(null);
  const [loadingTeamInfo, setLoadingTeamInfo] = React.useState(false);
  const [availablePrivacyTypes, setAvailablePrivacyTypes] = React.useState<TeamPrivacyType[]>([]);
  const [teamsAppId, setTeamsAppId] = React.useState<string>("");
  const [ownerDetails, setOwnerDetails] = React.useState<{ id: string; displayName: string; email: string } | null>(null);
  const [quotaModels, setQuotaModels] = React.useState<QuotaModel[]>([]);
  const [storageQuotaMode, setStorageQuotaMode] = React.useState<StorageQuotaMode>('NoQuota');
  const [loadingQuotaModels, setLoadingQuotaModels] = React.useState(false);

  const [formData, setFormData] = React.useState<TeamRequest>({
    teamName: "",
    description: "",
    privacy: "private",
    justification: "",
    owner: "",
    members: "",
    templateId: "",
    quotaModelId: "",
    requesterId: currentUserId,
    requesterEmail: currentUserEmail,
    teamId: teamId,
    teamsAppId: "", // 🔗 DEEP LINK: ID de l'application Teams
  });

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitMessage, setSubmitMessage] = React.useState<{
    type: "success" | "error" | "warning";
    text: string;
  } | null>(null);
  const [teamNameCheck, setTeamNameCheck] = React.useState<{
    checking: boolean;
    exists: boolean;
    existingTeam: any | null;
  }>({
    checking: false,
    exists: false,
    existingTeam: null
  });

  // Update formData when user info changes
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      requesterId: currentUserId,
      requesterEmail: currentUserEmail
    }));
  }, [currentUserId, currentUserEmail]);

  // Update formData.teamId when prop changes
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      teamId: teamId
    }));
  }, [teamId]);

  // Load templates and privacy types on component mount
  React.useEffect(() => {
    loadTemplates();
    loadPrivacyTypes();
    loadQuotaConfig();
    if (teamId) {
      loadTeamInfo();
    }
    
    // 🔗 DEEP LINK: Récupérer le Teams App ID depuis les variables d'environnement
    // ⚠️ IMPORTANT: Utiliser VITE_TEAMS_APP_ID (depuis manifest.json) et non appLaunchId
    // appLaunchId est un identifiant unique généré pour chaque lancement, pas l'ID de l'app
    const appId = import.meta.env.VITE_TEAMS_APP_ID || "";
    
    console.log("[TeamRequestForm] VITE_TEAMS_APP_ID value:", appId);
    console.log("[TeamRequestForm] import.meta.env:", import.meta.env);
    
    if (appId && appId.trim().length > 0) {
      setTeamsAppId(appId);
      
      // Mettre à jour le formData avec le teamsAppId
      setFormData(prev => ({
        ...prev,
        teamsAppId: appId
      }));
      
      console.log("[TeamRequestForm] Teams App ID from environment:", appId);
    } else {
      console.warn("[TeamRequestForm] VITE_TEAMS_APP_ID not configured or empty in environment");
      console.warn("[TeamRequestForm] Make sure TEAMS_APP_ID is set in env/.env.local");
    }
  }, [teamId]);

  React.useEffect(() => {
    if (!loadingTemplates && !loadingQuotaModels && !loadingTeamInfo) {
      telemetryService.capture('request_form_opened', currentUserId, {
        properties: {
          templates_count: templates.length,
          quota_models_count: quotaModels.length,
          quota_mode: storageQuotaMode,
          privacy_types_count: availablePrivacyTypes.length,
        }
      });
    }
  }, [loadingTemplates, loadingQuotaModels, loadingTeamInfo, templates.length, quotaModels.length, storageQuotaMode, availablePrivacyTypes.length, currentUserId]);

  // Check if team name already exists (debounced)
  React.useEffect(() => {
    const checkTeamName = async () => {
      const trimmedName = formData.teamName.trim();

      // Ne vérifier que si le nom a au moins 3 caractères
      if (trimmedName.length < 3) {
        setTeamNameCheck({ checking: false, exists: false, existingTeam: null });
        return;
      }

      setTeamNameCheck(prev => ({ ...prev, checking: true }));

      try {
        const checkStartTime = performance.now();
        const response = await apiService.checkTeamName(trimmedName);
        if (response.success && response.data) {
          setTeamNameCheck({
            checking: false,
            exists: response.data.exists,
            existingTeam: response.data.team
          });
          telemetryService.capture('request_team_name_checked', currentUserId, {
            properties: {
              exists: response.data.exists,
              team_name_length: trimmedName.length,
              checking_duration_ms: Math.round(performance.now() - checkStartTime),
            }
          });
        }
      } catch (error) {
        console.error("Error checking team name:", error);
        setTeamNameCheck({ checking: false, exists: false, existingTeam: null });
      }
    };

    // Debounce: attendre 500ms après la dernière frappe
    const timeoutId = setTimeout(() => {
      checkTeamName();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.teamName]);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await apiService.getEnabledTemplates();
      if (response.success && response.data) {
        setTemplates(response.data);
      }
    } catch (error) {
      console.error("Error loading templates:", error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadQuotaConfig = async () => {
    setLoadingQuotaModels(true);
    try {
      const settingsResp = await apiService.getSettings();
      const mode: StorageQuotaMode =
        settingsResp.success && settingsResp.data?.storageQuotaMode
          ? settingsResp.data.storageQuotaMode
          : 'NoQuota';
      setStorageQuotaMode(mode);

      if (mode !== 'NoQuota') {
        const modelsResp = await apiService.listQuotaModels();
        if (modelsResp.success && modelsResp.data) {
          setQuotaModels(modelsResp.data);

          const defaultModel = modelsResp.data.find(m => m.isDefault);
          const preselect =
            mode === 'QuotaMandatory'
              ? (defaultModel?.id ?? modelsResp.data[0]?.id ?? "")
              : (defaultModel?.id ?? "");
          if (preselect) {
            setFormData(prev => ({ ...prev, quotaModelId: preselect }));
          }
        }
      }
    } catch (error) {
      // Silencieux : si l'utilisateur n'a pas accès aux endpoints (Bearer requis),
      // on reste en mode 'NoQuota' et le champ ne s'affiche pas.
      console.warn('[TeamRequestForm] Quota configuration unavailable:', error);
      setStorageQuotaMode('NoQuota');
    } finally {
      setLoadingQuotaModels(false);
    }
  };

  const loadPrivacyTypes = async () => {
    try {
      const response = await apiService.getEnabledTeamPrivacyTypes();
      if (response.success && response.data) {
        setAvailablePrivacyTypes(response.data);
        // Set default privacy to first enabled type
        if (response.data.length > 0 && !formData.privacy) {
          setFormData(prev => ({
            ...prev,
            privacy: response.data[0].id
          }));
        }
      }
    } catch (error) {
      console.error("Error loading privacy types:", error);
      // Fallback to default types if API fails - use empty label/description so the render path uses translation keys
      setAvailablePrivacyTypes([
        { id: 'private', label: '', description: '', icon: '🔒', graphVisibility: 'private' },
        { id: 'public', label: '', description: '', icon: '🌐', graphVisibility: 'public' }
      ]);
    }
  };

  const loadTeamInfo = async () => {
    if (!teamId) return;
    setLoadingTeamInfo(true);
    try {
      const response = await apiService.getTeamInfo(teamId);
      if (response.success && response.data) {
        setTeamInfo(response.data);
      }
    } catch (error) {
      console.error("Error loading team info:", error);
    } finally {
      setLoadingTeamInfo(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === 'templateId') {
      const selectedTemplate = templates.find(t => t.teamId === value);
      telemetryService.capture('request_form_template_selected', currentUserId, {
        properties: {
          template_id: value || '',
          template_name: selectedTemplate?.name || '',
          is_empty: !value,
        }
      });
    }
    if (name === 'privacy') {
      telemetryService.capture('request_form_privacy_selected', currentUserId, {
        properties: {
          privacy: value,
        }
      });
    }
    if (name === 'quotaModelId') {
      const selectedQuota = quotaModels.find(m => m.id === value);
      telemetryService.capture('request_form_quota_selected', currentUserId, {
        properties: {
          quota_model_id: value || '',
          quota_model_name: selectedQuota?.name || '',
          max_storage_gb: selectedQuota?.maxStorageGB || 0,
          is_empty: !value,
        }
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      // Debug logging
      console.log("Submitting request with teamId:", formData.teamId);
      console.log("Props teamId:", teamId);

      // Validation — collect all missing fields before capturing
      const missingFields: string[] = [];
      if (!formData.teamName.trim()) missingFields.push('teamName');
      if (!formData.description.trim()) missingFields.push('description');
      if (!formData.justification.trim()) missingFields.push('justification');
      if (teamNameCheck.exists) missingFields.push('teamName_taken');
      if (storageQuotaMode === 'QuotaMandatory' && !formData.quotaModelId) missingFields.push('quotaModelId');

      if (missingFields.length > 0) {
        telemetryService.capture('request_submission_validation_error', currentUserId, {
          properties: { missing_fields: missingFields }
        });
        if (missingFields.includes('teamName')) {
          throw new Error(t('teamRequestForm:validation.teamNameRequired', 'Le nom de l\'équipe est requis'));
        }
        if (missingFields.includes('teamName_taken')) {
          throw new Error(t('teamRequestForm:validation.teamNameTaken', 'Une équipe avec ce nom existe déjà. Veuillez choisir un nom différent.'));
        }
        if (missingFields.includes('description')) {
          throw new Error(t('teamRequestForm:validation.descriptionRequired', 'La description est requise'));
        }
        if (missingFields.includes('justification')) {
          throw new Error(t('teamRequestForm:validation.justificationRequired', 'La justification est requise'));
        }
        if (missingFields.includes('quotaModelId')) {
          throw new Error(t('teamRequestForm:validation.quotaRequired', 'Un modèle de quota doit être sélectionné.'));
        }
      }

       // Préparer les données de la demande avec les détails du propriétaire
       const requestData = {
         ...formData,
         owner: ownerDetails ? [ownerDetails] : formData.owner
       };

       // Send request to backend
       const response = await apiService.submitRequest(requestData, formData.teamId);

      if (!response.success) {
        telemetryService.capture('request_submission_failed', currentUserId, {
          properties: {
            error_message: response.error || 'Unknown error',
            has_template: !!formData.templateId,
            privacy: formData.privacy,
          }
        });
        throw new Error(response.error || t('teamRequestForm:submit.error', 'Error submitting request'));
      }

      telemetryService.capture('request_submitted', currentUserId, {
        properties: {
          request_id: (response.data as any)?.id || '',
          has_template: !!formData.templateId,
          template_id: formData.templateId || null,
          privacy: formData.privacy,
          has_quota: !!formData.quotaModelId,
          quota_model_id: formData.quotaModelId || null,
          team_name_length: formData.teamName.trim().length,
          description_length: formData.description.trim().length,
          justification_length: formData.justification.trim().length,
          owner_is_requester: ownerDetails?.id === currentUserId,
        }
      });

      // Display success with any warnings
      let message = t('teamRequestForm:submit.success', 'Your request has been submitted successfully! It will be reviewed shortly.');
      if (response.warnings && response.warnings.length > 0) {
        message += "\n\n" + t('common:common.warning', 'Warning') + ": " + response.warnings.join("\n");
      }

      setSubmitMessage({
        type: response.warnings && response.warnings.length > 0 ? "warning" : "success",
        text: message,
      });

      // Reset form
      setOwnerDetails(null);
      setFormData({
        teamName: "",
        description: "",
        privacy: "private",
        justification: "",
        owner: "",
        templateId: "",
        quotaModelId: "",
        requesterId: currentUserId,
        requesterEmail: currentUserEmail,
        teamId: teamId,
        members: "",
      });
    } catch (error) {
      setSubmitMessage({
        type: "error",
        text: error instanceof Error ? error.message : "Une erreur est survenue",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setOwnerDetails(null);
    setFormData({
      teamName: "",
      description: "",
      privacy: "private",
      justification: "",
      owner: "",
      templateId: "",
      quotaModelId: "",
      requesterId: currentUserId,
      requesterEmail: currentUserEmail,
      teamId: teamId,
      members: "",
    });
    setSubmitMessage(null);
  };

  const generateRandomTestData = () => {
    const adjectives = ["Innovante", "Dynamique", "Créative", "Collaborative", "Agile", "Moderne", "Efficace", "Stratégique"];
    const nouns = ["Marketing", "Ventes", "Innovation", "Projet", "Développement", "Communication", "Recherche", "Digital"];
    const years = ["2024", "2025", "Q1", "Q2", "H1", "H2"];

    const justifications = [
      "Cette équipe est nécessaire pour améliorer la collaboration interdépartementale",
      "Nous avons besoin d'un espace dédié pour coordonner nos activités",
      "Ce projet requiert une communication centralisée et efficace",
      "Pour faciliter le partage de ressources et la gestion de projet",
      "Afin d'optimiser nos processus et notre productivité collective"
    ];

    const descriptions = [
      "Espace collaboratif dédié à la coordination des initiatives stratégiques",
      "Hub central pour partager ressources, documents et meilleures pratiques",
      "Équipe projet pour piloter nos objectifs trimestriels",
      "Plateforme de communication pour synchroniser nos efforts",
      "Centre de collaboration pour nos activités opérationnelles"
    ];

    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
    const randomYear = years[Math.floor(Math.random() * years.length)];
    const randomJustification = justifications[Math.floor(Math.random() * justifications.length)];
    const randomDescription = descriptions[Math.floor(Math.random() * descriptions.length)];

    setFormData(prev => ({
      ...prev,
      teamName: `${randomAdjective} ${randomNoun} ${randomYear}`,
      description: randomDescription,
      privacy: "private",
      justification: randomJustification
    }));

    setSubmitMessage({
      type: "success",
      text: t('teamRequestForm:testData.success', 'Données de test générées ! Vous pouvez modifier les valeurs avant de soumettre.')
    });
  };

  return (
    <div className="fluent-form-container">
      {/* ✨ MULTI-TENANT: Warning if tenant error (for admins who can still access form) */}
      {tenantError && (
        <div className="fluent-tenant-warning">
          <div className="fluent-tenant-warning-icon">⚠️</div>
          <div>
            <strong className="fluent-tenant-warning-title">{t('teamRequestForm:tenantWarning.title', 'Configuration incomplète')}</strong>
            <div className="fluent-tenant-warning-text">
              {t('teamRequestForm:tenantWarning.message', 'L\'application n\'est pas correctement configurée. Les demandes soumises pourraient échouer.')}
              <br />
              <strong>{t('common:common.error', 'Erreur')} :</strong> {tenantError}
            </div>
          </div>
        </div>
      )}

      <NotificationModal
        isOpen={submitMessage !== null}
        type={submitMessage?.type || "success"}
        message={submitMessage?.text || ""}
        onClose={() => setSubmitMessage(null)}
      />

      <div className="fluent-card">
        <div className="fluent-card-header">
          <h2 className="fluent-title">
            <span className="fluent-icon">👥</span>
            {t('teamRequestForm:title', 'Demande de création d\'équipe Teams')}
          </h2>
        </div>

        <div className="fluent-card-content">
          <form onSubmit={handleSubmit} className="fluent-form">
            <div className="fluent-field">
              <label className="fluent-label">
                {t('teamRequestForm:fields.teamName.label', 'Nom de l\'équipe')} <span className="fluent-required">*</span>
              </label>
              <div className="fluent-input-container">
                <span className="fluent-input-icon">📄</span>
                <input
                  type="text"
                  name="teamName"
                  value={formData.teamName}
                  onChange={handleChange}
                  placeholder={t('teamRequestForm:fields.teamName.placeholder', 'Ex: Projet Marketing 2025')}
                  className="fluent-input"
                  maxLength={100}
                  required
                />
              </div>
              <div className="fluent-hint">{formData.teamName.length}/100 {t('teamRequestForm:fields.teamName.hint', 'caractères').split('/')[1]}</div>
              {teamNameCheck.checking && (
                <div className="fluent-hint fluent-hint-checking">
                  ⏳ {t('teamRequestForm:fields.teamName.checking', 'Vérification du nom d\'équipe...')}
                </div>
              )}
              {!teamNameCheck.checking && teamNameCheck.exists && (
                <div className="fluent-hint fluent-hint-error">
                  ⚠️ {t('teamRequestForm:fields.teamName.taken', 'Une équipe avec ce nom existe déjà. Veuillez choisir un nom différent.')}
                </div>
              )}
              {!teamNameCheck.checking && !teamNameCheck.exists && formData.teamName.trim().length >= 3 && (
                <div className="fluent-hint fluent-hint-success">
                  ✅ {t('teamRequestForm:fields.teamName.available', 'Ce nom est disponible')}
                </div>
              )}
            </div>

            <div className="fluent-field">
              <label className="fluent-label">
                {t('teamRequestForm:fields.description.label', 'Description')} <span className="fluent-required">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder={t('teamRequestForm:fields.description.placeholder', 'Décrivez l\'objectif de cette équipe...')}
                className="fluent-textarea"
                rows={4}
                maxLength={500}
                required
              />
              <div className="fluent-hint">{formData.description.length}/500 {t('teamRequestForm:fields.description.hint', 'caractères').split('/')[1]}</div>
            </div>

            {templates.length > 0 && (
              <div className="fluent-field">
                <label className="fluent-label">
                  {t('teamRequestForm:fields.template.label', 'Modèle d\'équipe (optionnel)')}
                </label>
                <select
                  name="templateId"
                  value={formData.templateId}
                  onChange={handleChange}
                  className="fluent-select"
                  disabled={loadingTemplates}
                >
                  <option value="">{t('teamRequestForm:fields.template.blank', 'Équipe vierge (aucun modèle)')}</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.teamId}>
                      {template.name} - {template.description}
                    </option>
                  ))}
                </select>
                <div className="fluent-hint">
                  {t('teamRequestForm:fields.template.hint', 'Sélectionnez un modèle pour créer une équipe avec une structure prédéfinie')}
                </div>
              </div>
            )}

            <div className="fluent-field">
              <label className="fluent-label">
                {t('teamRequestForm:fields.privacy.label', 'Confidentialité')} <span className="fluent-required">*</span>
              </label>
              <select
                name="privacy"
                value={formData.privacy}
                onChange={handleChange}
                className="fluent-select"
                required
              >
                {availablePrivacyTypes.length > 0 ? (
                  availablePrivacyTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.icon} {t(`teamRequestForm:fields.privacy.${type.id}`, type.label)} - {t(`teamRequestForm:fields.privacy.${type.id}Description`, type.description)}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="private">🔒 {t('teamRequestForm:fields.privacy.private')} - {t('teamRequestForm:fields.privacy.privateDescription')}</option>
                    <option value="public">🌐 {t('teamRequestForm:fields.privacy.public')} - {t('teamRequestForm:fields.privacy.publicDescription')}</option>
                  </>
                )}
              </select>
            </div>

            {storageQuotaMode !== 'NoQuota' && (
              <div className="fluent-field">
                <label className="fluent-label">
                  {t('teamRequestForm:fields.quota.label', 'Quota de stockage')}
                  {storageQuotaMode === 'QuotaMandatory' && (
                    <span className="fluent-required">*</span>
                  )}
                </label>
                <select
                  name="quotaModelId"
                  value={formData.quotaModelId || ""}
                  onChange={handleChange}
                  className="fluent-select"
                  disabled={loadingQuotaModels || quotaModels.length === 0}
                  required={storageQuotaMode === 'QuotaMandatory'}
                >
                  {storageQuotaMode === 'QuotaOptional' && (
                    <option value="">
                      {t('teamRequestForm:fields.quota.blank', 'Aucun quota')}
                    </option>
                  )}
                  {quotaModels.map(model => (
                    <option key={model.id} value={model.id}>
                      {model.name} — {model.maxStorageGB} GB
                      {model.isDefault ? ` (${t('teamRequestForm:fields.quota.defaultLabel', 'défaut')})` : ''}
                    </option>
                  ))}
                </select>
                <div className="fluent-hint">
                  {storageQuotaMode === 'QuotaMandatory'
                    ? t('teamRequestForm:fields.quota.hintMandatory', 'Un quota de stockage est obligatoire pour chaque équipe.')
                    : t('teamRequestForm:fields.quota.hintOptional', 'Sélectionnez un quota ou laissez vide pour aucun quota.')}
                </div>
              </div>
            )}

            <div className="fluent-field">
              <label className="fluent-label">
                {t('teamRequestForm:fields.owner.label', 'Propriétaire de l\'équipe')} <span className="fluent-required">*</span>
              </label>
              <PeoplePicker
                selectedUser={formData.owner}
                onUserSelect={(user: User) => {
                  setFormData((prev) => ({
                    ...prev,
                    owner: user.displayName,
                  }));
                  // Capturer les détails du propriétaire
                  setOwnerDetails({
                    id: user.id,
                    displayName: user.displayName,
                    email: user.mail || user.userPrincipalName || ""
                  });
                  telemetryService.capture('request_form_owner_selected', currentUserId, {
                    properties: {
                      owner_is_requester: user.id === currentUserId,
                      owner_id: user.id,
                    }
                  });
                }}
                placeholder={t('teamRequestForm:fields.owner.placeholder', 'Rechercher un propriétaire...')}
                required
              />
              <div className="fluent-hint">{t('teamRequestForm:fields.owner.hint', 'La personne responsable de l\'équipe')}</div>
            </div>

            <div className="fluent-field">
              <label className="fluent-label">
                {t('teamRequestForm:fields.justification.label', 'Justification')} <span className="fluent-required">*</span>
              </label>
              <div className="fluent-textarea-container">
                <span className="fluent-textarea-icon">❓</span>
                <textarea
                  name="justification"
                  value={formData.justification}
                  onChange={handleChange}
                  placeholder={t('teamRequestForm:fields.justification.placeholder', 'Pourquoi cette équipe est-elle nécessaire ?')}
                  className="fluent-textarea"
                  rows={4}
                  maxLength={1000}
                  required
                />
              </div>
              <div className="fluent-hint">{formData.justification.length}/1000 {t('teamRequestForm:fields.justification.hint', 'caractères').split('/')[1]}</div>
            </div>

            <div className="fluent-button-group">
              <button
                type="submit"
                className="fluent-button fluent-button-primary fluent-button-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="fluent-button-spinner"></span>
                    {t('teamRequestForm:submit.submitting', 'Envoi en cours...')}
                  </>
                ) : (
                  t('teamRequestForm:submit.button', 'Soumettre la demande')
                )}
              </button>
              <button
                type="button"
                className="fluent-button fluent-button-secondary"
                onClick={resetForm}
                disabled={isSubmitting}
              >
                {t('teamRequestForm:buttons.reset', 'Réinitialiser')}
              </button>
              {/* <button
                type="button"
                className="fluent-button fluent-button-secondary"
                onClick={generateRandomTestData}
                disabled={isSubmitting}
                title={t('teamRequestForm:testData.button', 'Génère automatiquement des données de test')}
              >
                {t('teamRequestForm:buttons.test', '🎲 Test')}
              </button> */}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
